import { RAvailUrlProvider } from '../../common/RAvailUrlProvider';
import { FXPServiceCatalog, FxpServiceFactory } from '@fxp/fxpservices-di';
import { FxpHttpService } from '@fxp/fxpservices';
import {
  DSEAddUpdateRequest,
  DSEContractHoursDetails,
  DSEContractHoursResponse,
  DSEContractRequest,
  ForecastResponse,
} from '../contracts/DSEContracts';

/**
 * DSE capacity-hours service (FM dashboard migration).
 *
 * Self-dependent: talks to the RAvail-owned `/api/ce/*` endpoints via FXP `FxpHttpService`
 * (React projects use FxpHttpService directly, not the Angular `HttpAdapter`). Matches the
 * singleton pattern used by `Calendar.service.ts`. FXP is only resolved when `getSingleton()`
 * is first called, so tests can inject a mock `IDSEService` and never touch the FXP shell.
 */
export interface IDSEService {
  getDSECapacityHours: (aliases: string[]) => Promise<DSEContractHoursResponse>;
  addUpdateDSECapacityHours: (
    records: DSEContractHoursDetails[]
  ) => Promise<ForecastResponse>;
}

const JSON_HEADERS = { 'Content-Type': 'application/json;' };

const DSEService = (function () {
  let _instance: IDSEService;

  function createInstance(): IDSEService {
    const urlProvider = new RAvailUrlProvider();
    const httpService = FxpServiceFactory.getServiceInstance(
      FXPServiceCatalog.FxpHttpService
    ) as FxpHttpService;

    const getDSECapacityHours = async (
      aliases: string[]
    ): Promise<DSEContractHoursResponse> => {
      const url = urlProvider.GetDSECapacityHoursUrl();
      const payload: DSEContractRequest = {
        alias: aliases.map((a) => a.toLowerCase()),
      };
      const response = await httpService.post(url, payload, JSON_HEADERS);
      return response?.data as DSEContractHoursResponse;
    };

    const addUpdateDSECapacityHours = async (
      records: DSEContractHoursDetails[]
    ): Promise<ForecastResponse> => {
      const url = urlProvider.AddUpdateDSECapacityHoursUrl();
      const payload: DSEAddUpdateRequest = { upsertRecords: records };
      const response = await httpService.post(url, payload, JSON_HEADERS);
      return response?.data as ForecastResponse;
    };

    return { getDSECapacityHours, addUpdateDSECapacityHours };
  }

  return {
    getSingleton: (): IDSEService => {
      if (!_instance) {
        _instance = createInstance();
      }
      return _instance;
    },
  };
})();

export default DSEService;
